export type ConnectivityStatus = 'online' | 'offline'

export type ConnectivityListener = (status: ConnectivityStatus) => void

/**
 * Surveille l'état de la connexion réseau.
 *
 * - Utilise navigator.onLine comme valeur initiale
 * - Écoute les événements window 'online' / 'offline'
 * - Permet d'abonner des listeners (pattern observable léger)
 */
export class ConnectivityManager {
  private status: ConnectivityStatus
  private readonly listeners = new Set<ConnectivityListener>()

  constructor() {
    this.status = this.readOnlineStatus()

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline)
      window.addEventListener('offline', this.handleOffline)
    }
  }

  get isOnline(): boolean {
    return this.status === 'online'
  }

  get isOffline(): boolean {
    return this.status === 'offline'
  }

  /**
   * Abonne un listener aux changements de connectivité.
   * Retourne une fonction de désabonnement.
   */
  subscribe(listener: ConnectivityListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Libère les event listeners sur window. */
  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline)
      window.removeEventListener('offline', this.handleOffline)
    }
    this.listeners.clear()
  }

  private readOnlineStatus(): ConnectivityStatus {
    if (typeof navigator === 'undefined' || navigator.onLine === undefined) {
      return 'online' // SSR / environnement sans navigator : on suppose online
    }
    return navigator.onLine ? 'online' : 'offline'
  }

  private readonly handleOnline = (): void => {
    this.status = 'online'
    this.emit('online')
  }

  private readonly handleOffline = (): void => {
    this.status = 'offline'
    this.emit('offline')
  }

  private emit(status: ConnectivityStatus): void {
    for (const listener of this.listeners) {
      try {
        listener(status)
      } catch {
        // Un listener ne doit pas faire planter les autres
      }
    }
  }
}
